import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useEntitlementList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refEntitlementsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'id',
      sortable: true,
    },
    {
      key: 'name',
      label: 'Entitlement',
      sortable: true,
    },
    {
      key: 'fileID',
      label: 'Related file',
      sortable: false,
    },
    {
      key: 'schemeID',
      label: 'Scheme',
      sortable: false,
    },
    {
      key: 'internalRemarks',
      label: 'internal remarks',
      sortable: true,
      thStyle: { width: '20%' },
    },
    {
      key: 'validUntil',
      label: 'valid until',
      sortable: true,
    },
    { key: 'status', sortable: true },
    {
      key: 'createdAt',
      label: 'Created By',
      sortable: false,
    },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalEntitlements = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'createdAt')
  const isSortDirDesc = ref(route.query.sortDesc !== undefined ? route.query.sortDesc === 'true' : true)
  const statusFilter = ref([])
  const typeFilter = ref([])
  const paidEntitlementFilter = ref([])
  const schemeFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  if (Array.isArray(route.query.scheme)) {
    schemeFilter.value = route.query.scheme
  } else if (route.query.status) {
    schemeFilter.value = [route.query.scheme]
  }
  if (Array.isArray(route.query.entitlementType)) {
    typeFilter.value = route.query.entitlementType
  } else if (route.query.entitlementType) {
    typeFilter.value = [route.query.entitlementType]
  }
  if (Array.isArray(route.query.paidEntitlement)) {
    paidEntitlementFilter.value = route.query.paidEntitlement
  } else if (route.query.status) {
    paidEntitlementFilter.value = [route.query.paidEntitlement]
  }

  const toDeletedID = ref(null)
  const type = ref('all-entitlement')
  const entitlementData = ref([])
  const startDateFilter = ref(route.query.startDate || '')
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refEntitlementsTable.value ? refEntitlementsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalEntitlements.value,
    }
  })

  const refetchData = () => {
    refEntitlementsTable.value.refresh()
  }

  let timer = null
  watch([searchQuery, statusFilter, startDateFilter, schemeFilter, typeFilter, paidEntitlementFilter], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      refetchData()
    }, 1000)
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchEntitlements = (ctx, callback) => {
    store
      .dispatch('customers-all-entitlements/fetchEntitlements', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        paidEntitlement: paidEntitlementFilter.value,
        entitlementType: typeFilter.value,
        scheme: schemeFilter.value,
        startDate: startDateFilter.value,
        type: type.value,
      },
      { root: true })
      .then(response => {
        const { entitlements, total } = response.data
        entitlementData.value = entitlements
        callback(entitlements)
        totalEntitlements.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              paidEntitlement: paidEntitlementFilter.value,
              entitlementType: typeFilter.value,
              scheme: schemeFilter.value,
              startDate: startDateFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteEntitlement = () => {
    store
      .dispatch('customers-all-entitlements/deleteEntitlement', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveEntitlementStatusVariant = status => {
    if (status === 'redeemable') return 'success'
    if (status === 'redeemed') return 'primary'
    if (status === 'used') return 'primary'
    if (status === 'expired') return 'secondary'
    if (status === 'cancelled') return 'danger'
    return 'primary'
  }

  return {
    fetchEntitlements,
    tableColumns,
    perPage,
    currentPage,
    totalEntitlements,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refEntitlementsTable,

    resolveEntitlementStatusVariant,
    refetchData,

    toDeletedID,
    deleteEntitlement,
    entitlementData,

    // Extra Filters
    statusFilter,
    typeFilter,
    schemeFilter,
    paidEntitlementFilter,
    startDateFilter,
  }
}
